import { useState } from 'react'
import { useAccountSettings, useUpdateAccountSettings } from '@/hooks/useAccountSettings'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { SettingsSaveError } from '@/lib/settingsSaveError'
import type { ExpenseCategory } from '@/types'

export type { ExpenseCategory } from '@/types'

export const DEFAULT_EXPENSE_CATEGORIES: ExpenseCategory[] = [
  { key: 'repairs', en: 'Repairs & Maintenance', th: 'ค่าซ่อมบำรุง' },
  { key: 'utilities', en: 'Utilities (Common Area)', th: 'ค่าน้ำ-ไฟส่วนกลาง' },
  { key: 'insurance', en: 'Insurance', th: 'ค่าประกันภัย' },
  { key: 'property_tax', en: 'Property Tax', th: 'ภาษีที่ดินและสิ่งปลูกสร้าง' },
  { key: 'labor', en: 'Labor', th: 'ค่าแรงงาน' },
  { key: 'supplies', en: 'Supplies', th: 'ค่าวัสดุสิ้นเปลือง' },
  { key: 'cleaning', en: 'Cleaning', th: 'ค่าทำความสะอาด' },
  { key: 'security', en: 'Security', th: 'ค่ารักษาความปลอดภัย' },
  { key: 'management_fee', en: 'Management Fee', th: 'ค่าบริหารจัดการ' },
  { key: 'office_equipment', en: 'Office Equipment', th: 'อุปกรณ์สำนักงาน' },
  { key: 'other', en: 'Other', th: 'อื่นๆ' },
  { key: 'deposit_return', en: 'Deposit Return', th: 'คืนเงินประกัน' },
]

export function useExpenseCategorySettings() {
  const { data: account } = useAccountSettings()
  const updateSettings = useUpdateAccountSettings()
  const translateError = useApiErrorTranslator('settings')
  // PAY-1 (T149-084): no client validation here (none invented), so this is
  // only ever set on a server failure. Cleared on the next save attempt.
  const [saveError, setSaveError] = useState<SettingsSaveError | null>(null)

  // null means "not yet edited by user" — use server data
  const [localCategories, setLocalCategories] = useState<ExpenseCategory[] | null>(null)
  const [newCatEn, setNewCatEn] = useState('')
  const [newCatTh, setNewCatTh] = useState('')

  // Derive current categories: prefer local edits, fall back to server data
  const saved = account?.settings?.expense_categories
  const glDefaults = account?.expense_category_gl_defaults
  const applyGlDefaults = (values: ExpenseCategory[]): ExpenseCategory[] => values.map((category) => {
    // Deposit returns use their dedicated liability posting and ignore expense GL mappings.
    if (category.key === 'deposit_return') return category
    const code = category.gl_code?.trim() || glDefaults?.[category.key] || glDefaults?.other
    return code ? { ...category, gl_code: code } : category
  })
  const serverCategories =
    saved && Array.isArray(saved) && saved.length > 0
      ? saved
      : DEFAULT_EXPENSE_CATEGORIES
  const categories = applyGlDefaults(localCategories ?? serverCategories)

  const setCategories = (updater: ExpenseCategory[] | ((prev: ExpenseCategory[]) => ExpenseCategory[])) => {
    setLocalCategories((prev) => {
      const current = applyGlDefaults(prev ?? serverCategories)
      return typeof updater === 'function' ? updater(current) : updater
    })
  }

  const addCategory = () => {
    const enTrimmed = newCatEn.trim()
    const thTrimmed = newCatTh.trim()
    if (!enTrimmed) return
    const key = enTrimmed.toLowerCase().replace(/\s+/g, '_')
    if (categories.some((c) => c.key === key)) return
    setCategories((prev) => [...prev, { key, en: enTrimmed, th: thTrimmed || enTrimmed }])
    setNewCatEn('')
    setNewCatTh('')
  }

  const removeCategory = (index: number) => {
    setCategories((prev) => prev.filter((_, i) => i !== index))
  }

  const setGlCode = (index: number, code: string) => {
    setCategories((prev) => prev.map((category, i) =>
      i === index && category.key !== 'deposit_return' ? { ...category, gl_code: code } : category,
    ))
  }

  const resetCategories = () => {
    setLocalCategories(DEFAULT_EXPENSE_CATEGORIES)
  }

  // PAY-1 (T149-084): returns true only when the save actually happened.
  // The caller shows success only on true.
  const handleSave = async (): Promise<boolean> => {
    setSaveError(null)
    try {
      await updateSettings.mutateAsync({
        settings: { expense_categories: categories },
      })
      return true
    } catch (err) {
      setSaveError({ serverMessage: translateError(err) })
      return false
    }
  }

  return {
    categories,
    newCatEn,
    newCatTh,
    setNewCatEn,
    setNewCatTh,
    isPending: updateSettings.isPending,
    saveError,
    addCategory,
    removeCategory,
    setGlCode,
    resetCategories,
    handleSave,
  }
}
